import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Slider } from '../components/ui/slider';
import AppShell from '../components/AppShell';
import {
  ArrowLeft, Camera, Trash2, Loader2, Wand2, Download,
  Layers, Image as ImageIcon, Pencil, Star, Zap,
  SunMedium, Contrast, Droplets, Thermometer, Focus,
  RefreshCw, Play, Video, Mic, Footprints,
  Hand, Smile, Music2, Sparkles, Upload, Volume2, AudioLines
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FRAMINGS = [
  { value: 'headshot', label: 'Headshot' },
  { value: 'bust', label: 'Bust' },
  { value: 'waist', label: 'Waist' },
  { value: 'full_length', label: 'Full Length' },
];
const ORIENTATIONS = [
  { value: 'portrait', label: 'Portrait' },
  { value: 'landscape', label: 'Landscape' },
  { value: 'square', label: 'Square' },
];

const ANIMATION_TYPES = [
  { value: 'talk', label: 'Talk', icon: Mic, provider: 'heygen', desc: 'Realistic lip-sync speaking' },
  { value: 'gesture', label: 'Gesture', icon: Hand, provider: 'heygen', desc: 'Expressive hand gestures' },
  { value: 'emotion', label: 'Emotion', icon: Smile, provider: 'heygen', desc: 'Express feelings' },
  { value: 'walk', label: 'Walk', icon: Footprints, provider: 'sora', desc: 'Natural walking motion' },
  { value: 'dance', label: 'Dance', icon: Music2, provider: 'sora', desc: 'Fluid dance moves' },
  { value: 'custom', label: 'Custom', icon: Sparkles, provider: 'sora', desc: 'Any animation prompt' },
];

const EMOTIONS = [
  { value: 'happy', label: 'Happy' },
  { value: 'excited', label: 'Excited' },
  { value: 'sad', label: 'Sad' },
  { value: 'angry', label: 'Angry' },
  { value: 'surprised', label: 'Surprised' },
  { value: 'confident', label: 'Confident' },
  { value: 'thoughtful', label: 'Thoughtful' },
];

const InfluencerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [influencer, setInfluencer] = useState(null);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [training, setTraining] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [settingUpAnimation, setSettingUpAnimation] = useState(false);
  const [activeTab, setActiveTab] = useState('images');

  // Dialogs
  const [generateOpen, setGenerateOpen] = useState(false);
  const [modifyOpen, setModifyOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [animateOpen, setAnimateOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Generate form
  const [genForm, setGenForm] = useState({ prompt: '', framing: 'headshot', orientation: 'portrait' });
  // Modify form
  const [modForm, setModForm] = useState({ prompt: '', framing: 'headshot', orientation: 'portrait' });
  // Animate form
  const [animForm, setAnimForm] = useState({
    animation_type: 'talk', script: '', prompt: '',
    voice_id: '', emotion: '', orientation: 'portrait',
  });
  // Edit state
  const [edits, setEdits] = useState({
    brightness: 100, contrast: 100, saturation: 100,
    temperature: 0, sharpness: 0,
  });
  // Voice state
  const [cloningVoice, setCloningVoice] = useState(false);
  const [previewingVoice, setPreviewingVoice] = useState(false);
  const [deletingVoice, setDeletingVoice] = useState(false);
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null);
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [infRes, imgRes, vidRes] = await Promise.all([
        axios.get(`${API}/influencers/${id}`, { headers }),
        axios.get(`${API}/influencers/${id}/images`, { headers }),
        axios.get(`${API}/influencers/${id}/videos`, { headers }),
      ]);
      setInfluencer(infRes.data);
      setImages(imgRes.data);
      setVideos(vidRes.data);
    } catch {
      toast.error('Failed to load influencer');
      navigate('/influencers');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll for job completion
  const pollJob = async (jobId, onComplete, interval = 3000, maxAttempts = 120) => {
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, interval));
      try {
        const { data } = await axios.get(`${API}/influencers/jobs/${jobId}/status`, { headers });
        if (data.status === 'completed') { onComplete(data); return true; }
        if (data.status === 'failed') { toast.error(data.error || 'Generation failed'); return false; }
      } catch { /* continue */ }
    }
    toast.error('Generation timed out');
    return false;
  };

  // Generate new image (text-to-image)
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/influencers/${id}/images/generate`, {
        prompt: genForm.prompt || influencer?.prompt || '',
        framing: genForm.framing, orientation: genForm.orientation,
      }, { headers });
      toast.info('Generating image...');
      setGenerateOpen(false);
      const success = await pollJob(data.job_id, (result) => {
        if (result.image) {
          toast.success('Image generated!');
          setImages(prev => [result.image, ...prev]);
          setInfluencer(prev => prev ? { ...prev, avatar_url: result.image.image_url, image_count: (prev.image_count || 0) + 1 } : prev);
        }
      });
      if (!success) toast.error('Image generation failed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  // Modify image (image-to-image)
  const handleModify = async () => {
    if (!modForm.prompt.trim()) return toast.error('Describe what to change');
    setModifying(true);
    try {
      const { data } = await axios.post(`${API}/influencers/${id}/images/modify`, {
        source_image_url: selectedImage?.image_url,
        modification_prompt: modForm.prompt,
        framing: modForm.framing, orientation: modForm.orientation,
      }, { headers });
      toast.info('Modifying image...');
      setModifyOpen(false);
      await pollJob(data.job_id, (result) => {
        if (result.image) {
          toast.success('Modified image generated!');
          setImages(prev => [result.image, ...prev]);
        }
      });
      setModForm({ prompt: '', framing: 'headshot', orientation: 'portrait' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Modification failed');
    } finally { setModifying(false); }
  };

  // Train character consistency
  const handleTrain = async () => {
    setTraining(true);
    try {
      const { data } = await axios.post(`${API}/influencers/${id}/train`, { num_images: 6 }, { headers });
      toast.info(`Training started...`);
      await pollJob(data.job_id, () => {
        toast.success('Training complete!');
        fetchData();
      }, 5000, 200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Training failed');
    } finally { setTraining(false); }
  };

  // Setup animation (upload to HeyGen)
  const handleSetupAnimation = async () => {
    setSettingUpAnimation(true);
    try {
      const { data } = await axios.post(`${API}/influencers/${id}/setup-animation`, {}, { headers });
      toast.success(data.message || 'Animation setup complete!');
      setInfluencer(prev => prev ? {
        ...prev,
        animation_status: 'ready',
        heygen_talking_photo_id: data.talking_photo_id,
      } : prev);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Animation setup failed');
    } finally { setSettingUpAnimation(false); }
  };

  // Animate influencer
  const handleAnimate = async () => {
    const preset = ANIMATION_TYPES.find(a => a.value === animForm.animation_type);
    const needsScript = preset?.provider === 'heygen';
    if (needsScript && !animForm.script.trim()) return toast.error('Enter a script for your influencer to speak');
    if (!needsScript && !animForm.prompt.trim() && !animForm.script.trim()) return toast.error('Describe the animation');

    setAnimating(true);
    try {
      const { data } = await axios.post(`${API}/influencers/${id}/animate`, {
        animation_type: animForm.animation_type,
        script: animForm.script,
        prompt: animForm.prompt,
        voice_id: animForm.voice_id || undefined,
        emotion: animForm.emotion || undefined,
        orientation: animForm.orientation,
      }, { headers });
      toast.info(`Generating ${animForm.animation_type} animation...`);
      setAnimateOpen(false);
      await pollJob(data.job_id, (result) => {
        if (result.result?.video) {
          toast.success('Animation created!');
          setVideos(prev => [result.result.video, ...prev]);
          setInfluencer(prev => prev ? { ...prev, video_count: (prev.video_count || 0) + 1 } : prev);
          setActiveTab('videos');
        }
      }, 5000, 180);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Animation failed');
    } finally { setAnimating(false); }
  };

  // Set reference image
  const handleSetReference = async (img) => {
    try {
      await axios.put(`${API}/influencers/${id}/images/${img.id}/set-reference`, {}, { headers });
      toast.success('Reference image updated');
      setInfluencer(prev => prev ? { ...prev, reference_image_url: img.image_url, avatar_url: img.image_url } : prev);
    } catch { toast.error('Failed to update reference'); }
  };

  // Delete image
  const handleDeleteImage = async (imgId) => {
    try {
      await axios.delete(`${API}/influencers/${id}/images/${imgId}`, { headers });
      setImages(prev => prev.filter(i => i.id !== imgId));
      setInfluencer(prev => prev ? { ...prev, image_count: Math.max(0, (prev.image_count || 1) - 1) } : prev);
      toast.success('Image deleted');
    } catch { toast.error('Failed to delete'); }
  };

  // Delete video
  const handleDeleteVideo = async (vidId) => {
    try {
      await axios.delete(`${API}/influencers/${id}/videos/${vidId}`, { headers });
      setVideos(prev => prev.filter(v => v.id !== vidId));
      setInfluencer(prev => prev ? { ...prev, video_count: Math.max(0, (prev.video_count || 1) - 1) } : prev);
      toast.success('Video deleted');
    } catch { toast.error('Failed to delete'); }
  };

  // Image editing
  const getFilterStyle = (e = edits) => ({
    filter: [
      `brightness(${e.brightness}%)`,
      `contrast(${e.contrast}%)`,
      `saturate(${e.saturation}%)`,
      e.temperature > 0 ? `sepia(${e.temperature}%)` : '',
      e.sharpness > 0 ? `contrast(${100 + e.sharpness * 0.3}%)` : '',
    ].filter(Boolean).join(' '),
  });

  const resetEdits = () => setEdits({ brightness: 100, contrast: 100, saturation: 100, temperature: 0, sharpness: 0 });

  const downloadEditedImage = () => {
    if (!selectedImage) return;
    const canvas = document.createElement('canvas');
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.filter = getFilterStyle().filter;
      ctx.drawImage(img, 0, 0);
      const link = document.createElement('a');
      link.download = `${influencer?.name || 'influencer'}_edited.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = selectedImage.image_url;
    toast.success('Downloading edited image...');
  };

  const downloadImage = (img) => {
    const link = document.createElement('a');
    link.href = img.image_url;
    link.download = `${influencer?.name || 'image'}_${img.framing}.png`;
    link.target = '_blank';
    link.click();
  };

  // Voice cloning handlers
  const handleCloneVoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCloningVoice(true);
    try {
      const formData = new FormData();
      formData.append('audio_file', file);
      formData.append('voice_name', influencer.name || 'AI Influencer');
      const { data } = await axios.post(`${API}/influencers/${id}/clone-voice`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data.message || 'Voice cloned!');
      setInfluencer(prev => prev ? {
        ...prev, cloned_voice_id: data.voice_id, cloned_voice_name: data.voice_name,
      } : prev);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Voice cloning failed');
    } finally {
      setCloningVoice(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePreviewVoice = async () => {
    setPreviewingVoice(true);
    try {
      const { data } = await axios.post(`${API}/influencers/${id}/voice/preview`, {}, { headers });
      if (data.audio_url) {
        setPreviewAudioUrl(data.audio_url);
        const audio = new Audio(data.audio_url);
        audio.play();
        toast.success('Playing voice preview');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally { setPreviewingVoice(false); }
  };

  const handleDeleteVoice = async () => {
    setDeletingVoice(true);
    try {
      await axios.delete(`${API}/influencers/${id}/voice`, { headers });
      toast.success('Cloned voice removed');
      setInfluencer(prev => prev ? { ...prev, cloned_voice_id: null, cloned_voice_name: null } : prev);
      setPreviewAudioUrl(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete voice');
    } finally { setDeletingVoice(false); }
  };

  if (loading) return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-white/30" />
      </div>
    </AppShell>
  );

  if (!influencer) return null;

  const isRef = (img) => img.image_url === influencer.reference_image_url;
  const animReady = influencer.animation_status === 'ready';
  const currentPreset = ANIMATION_TYPES.find(a => a.value === animForm.animation_type);
  const needsScript = currentPreset?.provider === 'heygen';

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="influencer-detail-page">
        {/* Header */}
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/influencers')} className="text-white/50 hover:text-white" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {influencer.avatar_url ? (
                <img src={influencer.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover border border-white/10" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-white/[0.04] flex items-center justify-center">
                  <Camera className="w-5 h-5 text-white/20" />
                </div>
              )}
              <div className="min-w-0">
                <h1 className="text-lg font-semibold truncate">{influencer.name}</h1>
                <p className="text-xs text-white/35 truncate">
                  {influencer.niche || influencer.style?.style_type} &middot; {images.length} images &middot; {videos.length} videos
                </p>
              </div>
              {influencer.training_status === 'trained' && (
                <span className="px-2 py-0.5 bg-emerald-500/15 border border-emerald-500/25 rounded-full text-[10px] text-emerald-400 flex items-center gap-1">
                  <Zap className="w-2.5 h-2.5" /> Trained
                </span>
              )}
              {animReady && (
                <span className="px-2 py-0.5 bg-violet-500/15 border border-violet-500/25 rounded-full text-[10px] text-violet-400 flex items-center gap-1">
                  <Play className="w-2.5 h-2.5" /> Animated
                </span>
              )}
              {influencer.cloned_voice_id && (
                <span className="px-2 py-0.5 bg-emerald-500/15 border border-emerald-500/25 rounded-full text-[10px] text-emerald-400 flex items-center gap-1">
                  <AudioLines className="w-2.5 h-2.5" /> Voice
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button data-testid="train-btn" onClick={handleTrain} disabled={training} variant="outline" size="sm"
                className="border-white/[0.08] text-white/70 hover:text-white gap-1.5">
                {training ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}
                {training ? 'Training...' : 'Train'}
              </Button>
              <Button data-testid="animate-btn" onClick={() => {
                if (!animReady && !influencer.heygen_talking_photo_id) {
                  if (!influencer.reference_image_url && !influencer.avatar_url) {
                    toast.error('Generate an image first before animating');
                    return;
                  }
                  handleSetupAnimation();
                } else {
                  setAnimateOpen(true);
                }
              }} disabled={settingUpAnimation || animating} size="sm"
                className="bg-violet-600 hover:bg-violet-500 gap-1.5">
                {settingUpAnimation ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                {settingUpAnimation ? 'Setting up...' : animReady ? 'Animate' : 'Enable Animation'}
              </Button>
              <Button data-testid="generate-image-btn" onClick={() => { setGenForm(f => ({ ...f, prompt: influencer.prompt || '' })); setGenerateOpen(true); }}
                size="sm" className="bg-blue-600 hover:bg-blue-500 gap-1.5">
                <Wand2 className="w-3.5 h-3.5" /> Generate
              </Button>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className="flex gap-1 bg-white/[0.03] rounded-lg p-1 w-fit" data-testid="media-tabs">
            {[
              { key: 'images', label: 'Images', icon: ImageIcon, count: images.length },
              { key: 'videos', label: 'Videos', icon: Video, count: videos.length },
              { key: 'voice', label: 'Voice', icon: AudioLines, count: influencer.cloned_voice_id ? 1 : 0 },
            ].map(tab => (
              <button
                key={tab.key}
                data-testid={`tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab.key
                    ? 'bg-white/[0.08] text-white'
                    : 'text-white/40 hover:text-white/60'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                <span className="text-[10px] bg-white/[0.06] px-1.5 py-0.5 rounded-full">{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 py-4">
          {activeTab === 'images' && (
            /* Images Gallery */
            images.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24">
                <Camera className="w-10 h-10 text-white/10 mb-3" />
                <p className="text-white/40 mb-4">No images yet. Generate your first image.</p>
                <Button onClick={() => { setGenForm(f => ({ ...f, prompt: influencer.prompt || '' })); setGenerateOpen(true); }}
                  className="bg-blue-600 hover:bg-blue-500 gap-2">
                  <Wand2 className="w-4 h-4" /> Generate Image
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {images.map(img => (
                  <div key={img.id} data-testid={`image-card-${img.id}`}
                    className="group relative bg-[#12121a] border border-white/[0.06] rounded-lg overflow-hidden hover:border-white/[0.12] transition-all cursor-pointer"
                    onClick={() => { setSelectedImage(img); setViewerOpen(true); resetEdits(); }}>
                    <div className={`${img.orientation === 'landscape' ? 'aspect-video' : img.orientation === 'square' ? 'aspect-square' : 'aspect-[3/4]'} bg-[#0e0e16] overflow-hidden`}>
                      <img src={img.image_url} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                    </div>
                    <div className="absolute top-1.5 left-1.5 flex gap-1">
                      {isRef(img) && (
                        <span className="px-1.5 py-0.5 bg-amber-500/20 border border-amber-500/30 rounded text-[9px] text-amber-400 font-medium flex items-center gap-0.5">
                          <Star className="w-2 h-2" /> Ref
                        </span>
                      )}
                      {img.source_type === 'training' && <span className="px-1.5 py-0.5 bg-purple-500/20 border border-purple-500/30 rounded text-[9px] text-purple-400">Train</span>}
                      {img.source_type === 'image_to_image' && <span className="px-1.5 py-0.5 bg-cyan-500/20 border border-cyan-500/30 rounded text-[9px] text-cyan-400">Modified</span>}
                    </div>
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button onClick={(e) => { e.stopPropagation(); setSelectedImage(img); setModForm({ prompt: '', framing: img.framing, orientation: img.orientation }); setModifyOpen(true); }}
                        className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors" title="Modify"><Pencil className="w-4 h-4" /></button>
                      <button onClick={(e) => { e.stopPropagation(); setSelectedImage(img); setEditOpen(true); resetEdits(); }}
                        className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors" title="Edit"><SunMedium className="w-4 h-4" /></button>
                      <button onClick={(e) => { e.stopPropagation(); downloadImage(img); }}
                        className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors" title="Download"><Download className="w-4 h-4" /></button>
                      <button onClick={(e) => { e.stopPropagation(); handleSetReference(img); }}
                        className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors" title="Set Reference"><Star className="w-4 h-4" /></button>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteImage(img.id); }}
                        className="p-2 bg-white/10 rounded-lg hover:bg-red-500/30 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="px-2 py-1.5">
                      <p className="text-[10px] text-white/30 truncate">{img.framing} &middot; {img.orientation}</p>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
          {activeTab === 'videos' && (
            /* Videos Gallery */
            videos.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24">
                <Video className="w-10 h-10 text-white/10 mb-3" />
                <p className="text-white/40 mb-4">No animation videos yet.</p>
                <Button onClick={() => {
                  if (!animReady) {
                    if (!influencer.reference_image_url && !influencer.avatar_url) {
                      toast.error('Generate an image first');
                      return;
                    }
                    handleSetupAnimation();
                  } else {
                    setAnimateOpen(true);
                  }
                }} className="bg-violet-600 hover:bg-violet-500 gap-2" disabled={settingUpAnimation}>
                  {settingUpAnimation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  {animReady ? 'Create Animation' : 'Enable Animation'}
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {videos.map(vid => (
                  <div key={vid.id} data-testid={`video-card-${vid.id}`}
                    className="group relative bg-[#12121a] border border-white/[0.06] rounded-xl overflow-hidden hover:border-white/[0.12] transition-all">
                    <div className="aspect-[9/16] bg-[#0e0e16] relative">
                      {vid.thumbnail_url ? (
                        <img src={vid.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-8 h-8 text-white/10" />
                        </div>
                      )}
                      {vid.video_url && (
                        <a href={vid.video_url} target="_blank" rel="noopener noreferrer"
                          className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <Play className="w-6 h-6 text-white ml-0.5" />
                          </div>
                        </a>
                      )}
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                          vid.provider === 'heygen'
                            ? 'bg-violet-500/20 border-violet-500/30 text-violet-400'
                            : 'bg-orange-500/20 border-orange-500/30 text-orange-400'
                        }`}>
                          {vid.animation_type}
                        </span>
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-xs text-white/60 truncate">{vid.script || vid.prompt || vid.animation_type}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] text-white/25">
                          {vid.duration ? `${Math.round(vid.duration)}s` : ''} &middot; {vid.provider}
                        </span>
                        <div className="flex gap-1">
                          {vid.video_url && (
                            <a href={vid.video_url} target="_blank" rel="noopener noreferrer"
                              className="p-1.5 bg-white/[0.04] rounded-md hover:bg-white/[0.08] transition-colors" title="Watch">
                              <Play className="w-3 h-3 text-white/50" />
                            </a>
                          )}
                          <button onClick={() => handleDeleteVideo(vid.id)}
                            className="p-1.5 bg-white/[0.04] rounded-md hover:bg-red-500/20 transition-colors" title="Delete">
                            <Trash2 className="w-3 h-3 text-white/50" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* Voice Tab */}
          {activeTab === 'voice' && (
            <div className="max-w-2xl mx-auto py-8">
              <input type="file" ref={fileInputRef} accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac" onChange={handleCloneVoice} className="hidden" data-testid="voice-upload-input" />

              {influencer.cloned_voice_id ? (
                /* Voice cloned — show management */
                <div className="bg-[#12121a] border border-white/[0.06] rounded-xl p-6 space-y-5">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                      <AudioLines className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-white">{influencer.cloned_voice_name || 'Cloned Voice'}</h3>
                      <p className="text-xs text-white/40 mt-0.5">Voice ID: {influencer.cloned_voice_id.slice(0, 12)}...</p>
                      <span className="inline-block mt-1 px-2 py-0.5 bg-emerald-500/15 border border-emerald-500/25 rounded-full text-[10px] text-emerald-400 font-medium">Active</span>
                    </div>
                  </div>

                  <div className="bg-white/[0.02] rounded-lg p-4 border border-white/[0.04]">
                    <p className="text-xs text-white/50 mb-3">This voice is automatically used in all HeyGen animations (talk, gesture, emotion) for {influencer.name}.</p>
                    {previewAudioUrl && (
                      <audio controls src={previewAudioUrl} className="w-full mb-3 h-8" style={{ filter: 'invert(0.85)' }} data-testid="voice-preview-player" />
                    )}
                    <div className="flex gap-2">
                      <Button data-testid="preview-voice-btn" onClick={handlePreviewVoice} disabled={previewingVoice} variant="outline" size="sm"
                        className="border-white/[0.08] text-white/70 hover:text-white gap-1.5 flex-1">
                        {previewingVoice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
                        {previewingVoice ? 'Generating...' : 'Play Preview'}
                      </Button>
                      <Button data-testid="delete-voice-btn" onClick={handleDeleteVoice} disabled={deletingVoice} variant="outline" size="sm"
                        className="border-red-500/20 text-red-400 hover:bg-red-500/10 gap-1.5">
                        {deletingVoice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                /* No voice — show upload */
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-20 h-20 rounded-full bg-white/[0.02] border border-dashed border-white/[0.1] flex items-center justify-center mb-5">
                    <Mic className="w-8 h-8 text-white/15" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">Clone a Unique Voice</h3>
                  <p className="text-sm text-white/40 text-center max-w-sm mb-6">
                    Upload a clear audio sample (10-30 seconds) to give {influencer.name} a unique, consistent voice across all animations.
                  </p>
                  <div className="flex flex-col items-center gap-3">
                    <Button data-testid="clone-voice-btn" onClick={() => fileInputRef.current?.click()} disabled={cloningVoice}
                      className="bg-emerald-600 hover:bg-emerald-500 gap-2 h-11 px-6">
                      {cloningVoice ? <><Loader2 className="w-4 h-4 animate-spin" /> Cloning Voice...</> : <><Upload className="w-4 h-4" /> Upload Voice Sample</>}
                    </Button>
                    <p className="text-[10px] text-white/25">Supports MP3, WAV, M4A, OGG, FLAC</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Generate Image Dialog */}
        <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
          <DialogContent className="bg-[#14141e] border-white/[0.08] text-white max-w-md">
            <DialogHeader><DialogTitle>Generate New Image</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-sm text-white/60 mb-1 block">Prompt</Label>
                <Textarea data-testid="gen-prompt" value={genForm.prompt}
                  onChange={e => setGenForm(f => ({ ...f, prompt: e.target.value }))}
                  placeholder="Describe the image or leave empty to use default..."
                  className="bg-white/[0.04] border-white/[0.08] text-white min-h-[80px] placeholder:text-white/20" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-white/50 mb-1 block">Framing</Label>
                  <Select value={genForm.framing} onValueChange={v => setGenForm(f => ({ ...f, framing: v }))}>
                    <SelectTrigger className="bg-white/[0.04] border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1a24] border-white/[0.08]">
                      {FRAMINGS.map(f => <SelectItem key={f.value} value={f.value} className="text-white">{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-white/50 mb-1 block">Orientation</Label>
                  <Select value={genForm.orientation} onValueChange={v => setGenForm(f => ({ ...f, orientation: v }))}>
                    <SelectTrigger className="bg-white/[0.04] border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1a24] border-white/[0.08]">
                      {ORIENTATIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-white">{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="gen-submit" onClick={handleGenerate} disabled={generating}
                className="w-full bg-blue-600 hover:bg-blue-500 h-10 gap-2">
                {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Wand2 className="w-4 h-4" /> Generate</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modify Image Dialog */}
        <Dialog open={modifyOpen} onOpenChange={setModifyOpen}>
          <DialogContent className="bg-[#14141e] border-white/[0.08] text-white max-w-lg">
            <DialogHeader><DialogTitle>Modify Image</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              {selectedImage && (
                <div className="rounded-lg overflow-hidden border border-white/[0.06] bg-[#0e0e16]">
                  <img src={selectedImage.image_url} alt="" className="w-full max-h-48 object-contain" />
                </div>
              )}
              <div>
                <Label className="text-sm text-white/60 mb-1 block">What do you want to change?</Label>
                <Textarea data-testid="mod-prompt" value={modForm.prompt}
                  onChange={e => setModForm(f => ({ ...f, prompt: e.target.value }))}
                  placeholder="Change the outfit to a red dress, add a city background..."
                  className="bg-white/[0.04] border-white/[0.08] text-white min-h-[80px] placeholder:text-white/20" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-white/50 mb-1 block">Framing</Label>
                  <Select value={modForm.framing} onValueChange={v => setModForm(f => ({ ...f, framing: v }))}>
                    <SelectTrigger className="bg-white/[0.04] border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1a24] border-white/[0.08]">
                      {FRAMINGS.map(f => <SelectItem key={f.value} value={f.value} className="text-white">{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-white/50 mb-1 block">Orientation</Label>
                  <Select value={modForm.orientation} onValueChange={v => setModForm(f => ({ ...f, orientation: v }))}>
                    <SelectTrigger className="bg-white/[0.04] border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1a24] border-white/[0.08]">
                      {ORIENTATIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-white">{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="mod-submit" onClick={handleModify} disabled={modifying || !modForm.prompt.trim()}
                className="w-full bg-cyan-600 hover:bg-cyan-500 h-10 gap-2">
                {modifying ? <><Loader2 className="w-4 h-4 animate-spin" /> Modifying...</> : <><Pencil className="w-4 h-4" /> Modify Image</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Animate Dialog */}
        <Dialog open={animateOpen} onOpenChange={setAnimateOpen}>
          <DialogContent className="bg-[#14141e] border-white/[0.08] text-white max-w-lg">
            <DialogHeader><DialogTitle>Animate Influencer</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              {/* Animation Type Selector */}
              <div>
                <Label className="text-sm text-white/60 mb-2 block">Animation Type</Label>
                <div className="grid grid-cols-3 gap-2">
                  {ANIMATION_TYPES.map(a => (
                    <button key={a.value} data-testid={`anim-type-${a.value}`}
                      onClick={() => setAnimForm(f => ({ ...f, animation_type: a.value }))}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        animForm.animation_type === a.value
                          ? 'border-violet-500/50 bg-violet-500/10 text-violet-300'
                          : 'border-white/[0.06] bg-white/[0.02] text-white/50 hover:border-white/[0.12]'
                      }`}>
                      <a.icon className="w-5 h-5 mx-auto mb-1" />
                      <div className="text-xs font-medium">{a.label}</div>
                      <div className="text-[9px] opacity-50 mt-0.5">{a.provider}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Script (for HeyGen types) */}
              {needsScript && (
                <div>
                  <Label className="text-sm text-white/60 mb-1 block">Script</Label>
                  <Textarea data-testid="anim-script" value={animForm.script}
                    onChange={e => setAnimForm(f => ({ ...f, script: e.target.value }))}
                    placeholder="What should your influencer say?"
                    className="bg-white/[0.04] border-white/[0.08] text-white min-h-[80px] placeholder:text-white/20" />
                </div>
              )}

              {/* Custom prompt (for Sora types) */}
              {!needsScript && (
                <div>
                  <Label className="text-sm text-white/60 mb-1 block">Animation Prompt</Label>
                  <Textarea data-testid="anim-prompt" value={animForm.prompt}
                    onChange={e => setAnimForm(f => ({ ...f, prompt: e.target.value }))}
                    placeholder={animForm.animation_type === 'walk' ? 'Walking confidently through a modern city...'
                      : animForm.animation_type === 'dance' ? 'Dancing energetically in a studio...'
                      : 'Describe the animation you want...'}
                    className="bg-white/[0.04] border-white/[0.08] text-white min-h-[80px] placeholder:text-white/20" />
                </div>
              )}

              {/* Emotion (for HeyGen types) */}
              {needsScript && (
                <div>
                  <Label className="text-xs text-white/50 mb-1 block">Emotion</Label>
                  <Select value={animForm.emotion} onValueChange={v => setAnimForm(f => ({ ...f, emotion: v }))}>
                    <SelectTrigger className="bg-white/[0.04] border-white/[0.08] text-white">
                      <SelectValue placeholder="Natural (default)" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a24] border-white/[0.08]">
                      <SelectItem value="none" className="text-white">Natural</SelectItem>
                      {EMOTIONS.map(e => <SelectItem key={e.value} value={e.value} className="text-white">{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Orientation */}
              <div>
                <Label className="text-xs text-white/50 mb-1 block">Orientation</Label>
                <div className="grid grid-cols-3 gap-2">
                  {ORIENTATIONS.map(o => (
                    <button key={o.value}
                      onClick={() => setAnimForm(f => ({ ...f, orientation: o.value }))}
                      className={`p-2 rounded-lg border text-center transition-all text-xs ${
                        animForm.orientation === o.value
                          ? 'border-violet-500/50 bg-violet-500/10 text-violet-300'
                          : 'border-white/[0.06] bg-white/[0.02] text-white/50 hover:border-white/[0.12]'
                      }`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <Button data-testid="anim-submit" onClick={handleAnimate} disabled={animating}
                className="w-full bg-violet-600 hover:bg-violet-500 h-10 gap-2">
                {animating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Play className="w-4 h-4" /> Generate Animation</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Image Editor Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="bg-[#14141e] border-white/[0.08] text-white max-w-2xl">
            <DialogHeader><DialogTitle>Edit Image</DialogTitle></DialogHeader>
            <div className="grid grid-cols-[1fr_240px] gap-4 mt-2">
              <div className="rounded-lg overflow-hidden border border-white/[0.06] bg-[#0a0a0f] flex items-center justify-center min-h-[300px]">
                {selectedImage && (
                  <img src={selectedImage.image_url} alt="" className="max-w-full max-h-[400px] object-contain"
                    style={getFilterStyle()} crossOrigin="anonymous" />
                )}
              </div>
              <div className="space-y-3">
                {[
                  { key: 'brightness', label: 'Brightness', icon: SunMedium, min: 50, max: 150, default: 100 },
                  { key: 'contrast', label: 'Contrast', icon: Contrast, min: 50, max: 150, default: 100 },
                  { key: 'saturation', label: 'Saturation', icon: Droplets, min: 0, max: 200, default: 100 },
                  { key: 'temperature', label: 'Temperature', icon: Thermometer, min: 0, max: 50, default: 0 },
                  { key: 'sharpness', label: 'Sharpness', icon: Focus, min: 0, max: 50, default: 0 },
                ].map(({ key, label, icon: Icon, min, max }) => (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] text-white/50 flex items-center gap-1"><Icon className="w-3 h-3" />{label}</span>
                      <span className="text-[10px] text-white/30">{edits[key]}</span>
                    </div>
                    <Slider data-testid={`edit-${key}`} value={[edits[key]]} min={min} max={max} step={1}
                      onValueChange={([v]) => setEdits(e => ({ ...e, [key]: v }))} className="w-full" />
                  </div>
                ))}
                <div className="pt-2 space-y-2">
                  <Button onClick={resetEdits} variant="outline" size="sm" className="w-full border-white/[0.08] text-white/60 gap-1.5">
                    <RefreshCw className="w-3 h-3" /> Reset
                  </Button>
                  <Button onClick={downloadEditedImage} size="sm" className="w-full bg-blue-600 hover:bg-blue-500 gap-1.5">
                    <Download className="w-3 h-3" /> Download Edited
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Full Image Viewer */}
        <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
          <DialogContent className="bg-[#0a0a0f] border-white/[0.08] text-white max-w-4xl p-2">
            {selectedImage && (
              <div>
                <img src={selectedImage.image_url} alt="" className="w-full max-h-[70vh] object-contain rounded-lg" />
                <div className="flex items-center justify-between mt-3 px-2">
                  <div className="text-xs text-white/40">
                    {selectedImage.framing} &middot; {selectedImage.orientation} &middot; {selectedImage.source_type?.replace('_', ' ')}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="border-white/[0.08] text-white/60 gap-1"
                      onClick={() => { setModForm({ prompt: '', framing: selectedImage.framing, orientation: selectedImage.orientation }); setViewerOpen(false); setModifyOpen(true); }}>
                      <Pencil className="w-3 h-3" /> Modify
                    </Button>
                    <Button size="sm" variant="outline" className="border-white/[0.08] text-white/60 gap-1"
                      onClick={() => { setViewerOpen(false); setEditOpen(true); resetEdits(); }}>
                      <SunMedium className="w-3 h-3" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="border-white/[0.08] text-white/60 gap-1"
                      onClick={() => handleSetReference(selectedImage)}>
                      <Star className="w-3 h-3" /> Set Reference
                    </Button>
                    <Button size="sm" variant="outline" className="border-white/[0.08] text-white/60 gap-1"
                      onClick={() => downloadImage(selectedImage)}>
                      <Download className="w-3 h-3" /> Download
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
};

export default InfluencerDetailPage;
